<template>
  <div :class="classList">
    <slot name="label">
      <label class="st-form-group__label" v-if="label" :for="forId">
        {{ label }}
      </label>
    </slot>
    <slot name="description">
      <p
        v-if="description"
        :id="descriptionId"
        class="st-form-group__description"
      >
        {{ description }}
      </p>
    </slot>
    <slot
      :aria-describedby="useAriaDescribedby ? ariaDescribedby : null"
    ></slot>
    <span
      v-for="error in filteredErrors"
      :key="error.key"
      class="st-form-group__error-wrapper"
    >
      <span
        :id="error.id ? `${error.id}-error` : `${forId}-error`"
        class="st-form-group__error-wrapper__error"
        :role="useLiveRegion ? 'alert' : null"
        :aria-live="useLiveRegion ? 'assertive' : null"
        :aria-atomic="useLiveRegion ? 'true' : null"
      >
        <st-icon
          name="warning-triangle"
          size="small"
          class="st-form-group__error-wrapper__error__warning"
          :icon-props="{ color: '#CC4039' }"
        />
        {{ error.message }}
      </span>
    </span>
  </div>
</template>

<script>
import { computed } from "vue";
import StIcon from "../icon/Icon.vue";

export default {
  name: "st-form-group",
  components: {
    StIcon,
  },
  props: {
    forId: {
      type: String,
    },
    label: {
      type: String,
      default: "",
    },
    required: {
      type: Boolean,
      default: false,
    },
    validation: {
      type: Object,
    },
    validationErrors: {
      type: Array,
      default: () => {
        return [];
      },
    },
    description: {
      type: String,
    },
    descriptionId: {
      type: String,
    },
    useLiveRegion: {
      type: Boolean,
      default: true,
    },
    useAriaDescribedby: {
      type: Boolean,
      default: true,
    },
  },
  setup(props) {
    const classList = computed(() => {
      const formGroup = "st-form-group";
      return [
        formGroup,
        props.required && `${formGroup}--requried`,
        hasError.value && `${formGroup}--invalid`,
      ];
    });

    const hasError = computed(() => {
      if (!props.validation) return false;

      return props.validation.$error;
    });

    const filteredErrors = computed(() => {
      // Use validationErrors if prop is provided, filter by $invalid
      if (props.validationErrors && props.validationErrors.length > 0) {
        return props.validationErrors.filter(
          (error) => props.validation && props.validation[error.key]?.$invalid
        );
      }

      // Otherwise, get error messages from Vuelidate object
      if (props.validation && props.validation.$errors) {
        return props.validation.$errors.map((error, index) => ({
          key: error.$property,
          message: error.$message,
          id: `${props.forId}-${error.$uid}` || `error-${index}`,
        }));
      }

      return [];
    });

    const ariaDescribedby = computed(() => {
      const ids = [];

      // Add description ID if present
      if (props.descriptionId && props.description) {
        ids.push(props.descriptionId);
      }

      // Add error IDs for currently active validation errors
      if (hasError.value) {
        filteredErrors.value.forEach((error) => {
          const errorId = error.id
            ? `${error.id}-error`
            : `${props.forId}-error`;
          ids.push(errorId);
        });
      }

      return ids.length > 0 ? ids.join(" ") : null;
    });

    return { classList, hasError, ariaDescribedby, filteredErrors };
  },
};
</script>

<style lang="scss" scoped>
@import "../../scss/main.scss";

.st-form-group {
  &--requried {
    label:after {
      content: " - obligatoriskt";
      font-style: italic;
      font-weight: 400;
    }
  }

  &--invalid {
    .st-form-group__error-wrapper__error {
      display: flex;
      color: var(--danger-color-primary);
      align-items: center;
    }
  }

  &__label {
    font-size: rem-calc(14);
    font-weight: 600;
    font-family: var(--font-primary);
    color: var(--text-color-primary);
    display: inline-block;
    margin-bottom: rem-calc(4);
  }

  &__description {
    font-family: var(--font-primary);
    font-size: rem-calc(14);
    color: var(--text-color-primary);
    line-height: rem-calc(21);
    margin: 0 0 rem-calc(4) 0;
  }

  &__error-wrapper {
    &__error {
      display: none;
      font-size: rem-calc(14);
      font-weight: 600;
      font-family: var(--font-primary);
      margin-top: rem-calc(4);

      &__warning {
        background-size: rem-calc(14) rem-calc(14);
        display: inline-block;
        margin-right: rem-calc(6);
      }
    }
  }

  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.2s linear;
  }

  .fade-enter-from,
  .fade-leave-to {
    opacity: 0;
  }
}
</style>
